import React, { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { Menu, X, LogOut, Clock, ChevronDown } from "lucide-react";
import { Logo } from "@/components/Logo";
import { useLang } from "@/lib/i18n";
import { useAuth } from "@/context/AuthContext";
import { OfferBell } from "@/components/OfferBell";

const LangToggle = ({ compact = false }) => {
  const { lang, setLang } = useLang();
  return (
    <div data-testid="language-toggle" className="inline-flex items-center rounded-full bg-slate-100 p-1">
      {["en", "hi"].map((l) => (
        <button
          key={l}
          data-testid={`language-toggle-${l}`}
          onClick={() => setLang(l)}
          className={`min-h-[36px] rounded-full px-3.5 text-sm font-semibold transition-colors duration-200 ${
            lang === l ? "bg-brand text-white shadow-sm" : "text-slate-500 hover:text-brand"
          }`}
        >
          {l === "en" ? "EN" : "हिं"}
        </button>
      ))}
    </div>
  );
};

export const Header = () => {
  const { t } = useLang();
  const { user, login, logout } = useAuth();
  const [open, setOpen] = useState(false);
  const [userMenu, setUserMenu] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  const links = [
    { to: "/", label: t("nav_home") },
    { to: "/#categories", label: t("nav_categories") },
    { to: "/#how", label: t("nav_how") },
    { to: "/my-offers", label: t("mo_title") },
    { to: "/dealers", label: t("nav_dealers") },
    { to: "/about", label: t("nav_about") },
    { to: "/contact", label: t("nav_contact") },
  ];

  const goLink = (to) => {
    setOpen(false);
    if (to.startsWith("/#")) {
      const id = to.slice(2);
      if (location.pathname !== "/") {
        navigate("/");
        setTimeout(() => document.getElementById(id)?.scrollIntoView({ behavior: "smooth" }), 300);
      } else {
        document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
      }
    } else {
      navigate(to);
    }
  };

  return (
    <header className="sticky top-0 z-50 border-b border-slate-200/80 bg-white/90 backdrop-blur-lg">
      <div className="vlx-container flex h-16 items-center justify-between md:h-[72px]">
        <Logo />
        <nav className="hidden items-center gap-1 lg:flex">
          {links.map((l) => (
            <button
              key={l.to}
              data-testid={`nav-${l.label}`}
              onClick={() => goLink(l.to)}
              className="min-h-[40px] rounded-lg px-3 text-[15px] font-medium text-slate-600 transition-colors duration-200 hover:bg-brand-light hover:text-brand"
            >
              {l.label}
            </button>
          ))}
        </nav>

        <div className="flex items-center gap-2 sm:gap-3">
          <div className="hidden sm:block"><LangToggle /></div>
          <OfferBell t={t} />

          {user ? (
            <div className="relative">
              <button
                data-testid="user-menu-btn"
                onClick={() => setUserMenu((v) => !v)}
                className="flex min-h-[44px] items-center gap-2 rounded-full border border-slate-200 py-1 pl-1 pr-2.5 transition-colors hover:border-brand"
              >
                {user.picture ? (
                  <img src={user.picture} alt="" className="h-8 w-8 rounded-full object-cover" />
                ) : (
                  <span className="flex h-8 w-8 items-center justify-center rounded-full bg-brand text-sm font-bold text-white">
                    {user.name?.[0] || "U"}
                  </span>
                )}
                <ChevronDown className="h-4 w-4 text-slate-400" />
              </button>
              {userMenu && (
                <div className="absolute right-0 mt-2 w-52 overflow-hidden rounded-2xl border border-slate-200 bg-white py-1.5 shadow-xl">
                  <div className="border-b border-slate-100 px-4 py-2">
                    <p className="truncate text-sm font-semibold text-slate-800">{user.name}</p>
                    <p className="truncate text-xs text-slate-400">{user.email}</p>
                  </div>
                  <button
                    data-testid="menu-history-btn"
                    onClick={() => { setUserMenu(false); navigate("/history"); }}
                    className="flex w-full items-center gap-2.5 px-4 py-2.5 text-sm text-slate-600 hover:bg-slate-50"
                  >
                    <Clock className="h-4 w-4" /> {t("my_history")}
                  </button>
                  <button
                    data-testid="menu-logout-btn"
                    onClick={() => { setUserMenu(false); logout(); }}
                    className="flex w-full items-center gap-2.5 px-4 py-2.5 text-sm text-red-500 hover:bg-red-50"
                  >
                    <LogOut className="h-4 w-4" /> {t("logout")}
                  </button>
                </div>
              )}
            </div>
          ) : (
            <button
              data-testid="login-btn"
              onClick={login}
              className="hidden min-h-[44px] items-center rounded-full bg-brand px-5 text-sm font-semibold text-white shadow-sm transition-colors duration-200 hover:bg-brand-hover sm:inline-flex"
            >
              {t("login")}
            </button>
          )}

          <button
            data-testid="mobile-menu-btn"
            aria-label={open ? "Close menu" : "Open menu"}
            aria-expanded={open}
            onClick={() => setOpen((v) => !v)}
            className="flex h-11 w-11 items-center justify-center rounded-xl border border-slate-200 lg:hidden"
          >
            {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>
      </div>

      {open && (
        <div className="border-t border-slate-100 bg-white lg:hidden">
          <div className="vlx-container flex flex-col gap-1 py-3">
            {links.map((l) => (
              <button
                key={l.to}
                data-testid={`mobile-nav-${l.label}`}
                onClick={() => goLink(l.to)}
                className="min-h-[48px] rounded-lg px-3 text-left text-base font-medium text-slate-700 hover:bg-brand-light"
              >
                {l.label}
              </button>
            ))}
            <div className="flex items-center justify-between px-1 pt-2">
              <LangToggle />
              {!user && (
                <button
                  data-testid="mobile-login-btn"
                  onClick={login}
                  className="min-h-[44px] rounded-full bg-brand px-5 text-sm font-semibold text-white"
                >
                  {t("login")}
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </header>
  );
};
